import { GridComparatorFn } from '@mui/x-data-grid';
import { Favorite, ForecastEntry, ForecastYears, Quote, Quotes, StockRow } from '../types';
import { trendCalculate } from './trend';
import { getForecastTicker, toDividendPerShare } from './tickers';

/** Anos com projeção numa entrada do forecast, em ordem crescente (ex.: ["2026", "2027"]). */
export function getYears(entry?: ForecastEntry): string[] {
  if (!entry) return [];

  return Object.keys(entry).filter((key) => key !== 'ticker').sort();
}

/** Ano corrente e próximo ano da projeção; antes dos dados chegarem, usa o calendário. */
export function getForecastYears(forecast?: ForecastEntry[], today = new Date()): ForecastYears {
  const years = getYears(forecast?.[0]);

  if (!forecast || !years.length) {
    return {
      start: String(today.getFullYear()),
      end: String(today.getFullYear() + 1),
    };
  }

  return { start: years[0], end: years[1] };
}

/** "3.4% | 1.45": dividend yield seguido do dividendo por ação. */
function formatDividendYield(dividend: string | undefined, price: number | undefined, separator = ' | ') {
  return `${((Number(dividend) / Number(price)) * 100).toFixed(1)}%${separator}${dividend}`;
}

/** Ordena pelas colunas de DY ("3.4% | 1.45") usando só o percentual. */
export const percentageComparator: GridComparatorFn<string> = (v1, v2) => {
  const n1 = parseFloat(v1.split('% | ')?.at(0)?.replace('%', '') ?? '0');
  const n2 = parseFloat(v2.split('% | ')?.at(0)?.replace('%', '') ?? '0');
  return n1 - n2;
};

/** Valor numérico do DY de uma coluna formatada ("3.4% | 1.45" -> 3.4). */
export function parseDividendYield(value: string) {
  return parseFloat(value.replace('%', ''));
}

export function buildStockRow(
  ticker: string,
  quote: Quote,
  forecast: ForecastEntry[],
  favorites: Favorite[],
): StockRow {
  const forecastEntry = forecast.find((entry) => entry.ticker === getForecastTicker(ticker.replace('.SA', '')));
  const years = getYears(forecastEntry);
  const favorite = favorites.find((f) => f.ticker === ticker);

  const last = quote.dividend?.lastYear?.total?.toFixed(2) as string;
  const start = toDividendPerShare(ticker, forecastEntry?.[years[0]] ?? '0');
  const end = toDividendPerShare(ticker, forecastEntry?.[years[1]] ?? '0');
  const { price } = quote;

  return {
    id: ticker,
    ticker,
    quantity: favorite?.quantity?.toString() ?? '0',
    goal: favorite?.goal?.toString() ?? '0',
    last,
    start,
    end,
    price,
    changePct: `${Number(quote.changePct).toFixed(2)}%`,
    change: quote.change,
    score: quote.score,
    lastDY: formatDividendYield(last, price),
    startDY: formatDividendYield(start, price),
    // Mantém o espaço duplo que já existia no texto do DY do próximo ano.
    endDY: formatDividendYield(end, price, '  | '),
    description: trendCalculate(quote.position, quote.trend).description,
    logo: quote.logo,
    dividend: quote.dividend,
    currency: quote.currency,
  };
}

/** Linhas da tabela: todos os ativos com cotação, ou só os da carteira. */
export function buildStockRows(
  quotes: Quotes,
  forecast: ForecastEntry[],
  favorites: Favorite[],
  onlyFavorites: boolean,
): StockRow[] {
  return Object.keys(quotes)
    .filter((ticker) => !onlyFavorites || favorites.some((f) => f.ticker === ticker))
    .filter((ticker) => quotes[ticker]?.price)
    .map((ticker) => buildStockRow(ticker, quotes[ticker], forecast, favorites));
}
